import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dceAnalysisService } from '../services/dce-analysis.service';
import {
  type BulkValidateRequest,
  type DceAnalysis,
  type ListRequirementsFilters,
  isTerminalAnalysisStatus,
} from '../types/dce-analysis.types';

const QK = {
  current: (opportunityId: number) => ['tm', 'dce-analysis', opportunityId, 'current'],
  history: (opportunityId: number) => ['tm', 'dce-analysis', opportunityId, 'history'],
  historyDetail: (opportunityId: number, analysisId: number) => [
    'tm',
    'dce-analysis',
    opportunityId,
    'history',
    analysisId,
  ],
  requirements: (opportunityId: number, filters?: ListRequirementsFilters) => [
    'tm',
    'dce-analysis',
    opportunityId,
    'requirements',
    filters ?? {},
  ],
  profiles: (opportunityId: number) => ['tm', 'dce-analysis', opportunityId, 'profiles'],
  requiredDocuments: (opportunityId: number) => [
    'tm',
    'dce-analysis',
    opportunityId,
    'required-documents',
  ],
};

/**
 * Sprint 4.D — Analyse courante. Polling 2s tant que `status ∈ PENDING/RUNNING`.
 * Le hook ne lance PAS l'analyse — c'est `useTriggerDceAnalysis` qui le fait.
 */
export function useDceAnalysis(opportunityId: number | null | undefined) {
  return useQuery({
    queryKey: opportunityId
      ? QK.current(opportunityId)
      : ['tm', 'dce-analysis', 'noop'],
    queryFn: () => dceAnalysisService.getCurrent(opportunityId as number),
    enabled: !!opportunityId,
    refetchInterval: (query) => {
      const data = query.state.data as DceAnalysis | null | undefined;
      if (!data) return false;
      return isTerminalAnalysisStatus(data.status) ? false : 2000;
    },
  });
}

export function useDceAnalysisHistory(opportunityId: number | null | undefined) {
  return useQuery({
    queryKey: opportunityId
      ? QK.history(opportunityId)
      : ['tm', 'dce-analysis', 'history', 'noop'],
    queryFn: () => dceAnalysisService.getHistory(opportunityId as number),
    enabled: !!opportunityId,
  });
}

export function useDceAnalysisHistoryDetail(
  opportunityId: number | null | undefined,
  analysisId: number | null | undefined,
) {
  return useQuery({
    queryKey:
      opportunityId && analysisId
        ? QK.historyDetail(opportunityId, analysisId)
        : ['tm', 'dce-analysis', 'history-detail', 'noop'],
    queryFn: () =>
      dceAnalysisService.getHistoryDetail(opportunityId as number, analysisId as number),
    enabled: !!opportunityId && !!analysisId,
  });
}

export function useDceRequirements(
  opportunityId: number | null | undefined,
  filters: ListRequirementsFilters = {},
) {
  return useQuery({
    queryKey: opportunityId
      ? QK.requirements(opportunityId, filters)
      : ['tm', 'dce-analysis', 'requirements', 'noop'],
    queryFn: () =>
      dceAnalysisService.listRequirements(opportunityId as number, filters),
    enabled: !!opportunityId,
  });
}

export function useDceProfiles(opportunityId: number | null | undefined) {
  return useQuery({
    queryKey: opportunityId
      ? QK.profiles(opportunityId)
      : ['tm', 'dce-analysis', 'profiles', 'noop'],
    queryFn: () => dceAnalysisService.listProfiles(opportunityId as number),
    enabled: !!opportunityId,
  });
}

export function useDceRequiredDocuments(opportunityId: number | null | undefined) {
  return useQuery({
    queryKey: opportunityId
      ? QK.requiredDocuments(opportunityId)
      : ['tm', 'dce-analysis', 'required-documents', 'noop'],
    queryFn: () =>
      dceAnalysisService.listRequiredDocuments(opportunityId as number),
    enabled: !!opportunityId,
  });
}

export function useTriggerDceAnalysis(opportunityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (force?: boolean) =>
      dceAnalysisService.runAnalysis(opportunityId, force),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tm', 'dce-analysis', opportunityId] });
    },
  });
}

export function useUpdateDceRequirement(opportunityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: number;
      payload: Parameters<typeof dceAnalysisService.updateRequirement>[2];
    }) => dceAnalysisService.updateRequirement(opportunityId, input.id, input.payload),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['tm', 'dce-analysis', opportunityId, 'requirements'],
      });
      qc.invalidateQueries({ queryKey: QK.current(opportunityId) });
    },
  });
}

export function useBulkValidateRequirements(opportunityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BulkValidateRequest) =>
      dceAnalysisService.bulkValidateRequirements(opportunityId, payload),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['tm', 'dce-analysis', opportunityId, 'requirements'],
      });
      qc.invalidateQueries({ queryKey: QK.current(opportunityId) });
    },
  });
}

export function useUpdateDceProfile(opportunityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: number;
      payload: Parameters<typeof dceAnalysisService.updateProfile>[2];
    }) => dceAnalysisService.updateProfile(opportunityId, input.id, input.payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.profiles(opportunityId) });
      qc.invalidateQueries({ queryKey: QK.current(opportunityId) });
    },
  });
}

export function useUpdateDceRequiredDocument(opportunityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: number;
      payload: Parameters<typeof dceAnalysisService.updateRequiredDocument>[2];
    }) =>
      dceAnalysisService.updateRequiredDocument(opportunityId, input.id, input.payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.requiredDocuments(opportunityId) });
      qc.invalidateQueries({ queryKey: QK.current(opportunityId) });
    },
  });
}

export function useForceProfilesApplicable(opportunityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (forceApplicable: boolean) =>
      dceAnalysisService.forceProfilesApplicable(opportunityId, forceApplicable),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.current(opportunityId) });
    },
  });
}

/**
 * Mutation utilisée par le bouton "Ouvrir le document" sur les source links.
 * Pas de cache — chaque clic régénère une URL fraîche (expiration 15 min).
 */
export function useGetDocumentPresignedUrl() {
  return useMutation({
    mutationFn: (documentId: number) =>
      dceAnalysisService.getDocumentPresignedUrl(documentId),
  });
}
